import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * VendorCategoryHeatmap — which of one vendor's product areas land at this
 * agency, and when.
 *
 * Rows are security categories, columns are fiscal years, and a cell's ink is
 * its share of the biggest cell. It replaces a trajectory, so it keeps the
 * time axis and adds the dimension the trajectory could not carry: a bar chart
 * says "$1.8M in FY27" where this says "$1.8M of firewall in FY27, while SASE
 * tripled".
 *
 * WHY THE CHANNEL IS ON THE FACE
 * Palo Alto never appears as a supplier in California procurement. A heatmap
 * of its spend with no supplier named anywhere invites exactly the wrong
 * reading — that we sell this agency nothing — which is what the widget this
 * replaces actually said. `resellers` is therefore part of the chart, not a
 * tooltip.
 *
 * Presentational: data via props, no fetching, colours from the shared
 * security-category map so this and the purchase history agree.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { securityCategoryColor } from './securityCategories.js';
// A year column narrower than this clips its value; the label column needs
// this much to hold a category name. Both match the CSS rung in
// account-board.css, which is the fallback when even one column will not fit.
const COL_MIN = 56;
const LABEL_MIN = 88;
const defaultFormatValue = (v) => {
    if (v >= 1000000000)
        return `$${(v / 1000000000).toFixed(1)}B`;
    if (v >= 1000000)
        return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000)
        return `$${(v / 1000).toFixed(0)}K`;
    return `$${Math.round(v)}`;
};
const fyLabel = (fy) => `FY${String(fy).slice(-2)}`;
export const VendorCategoryHeatmap = ({ categories, years, cells, resellers = [], scopeNote, fill = false, height = 320, onPick, formatValue = defaultFormatValue, }) => {
    const { at, max, rowTotal } = useMemo(() => {
        const map = new Map();
        const totals = new Map();
        let biggest = 0;
        for (const c of cells || []) {
            map.set(`${c.category}::${c.fy}`, c);
            totals.set(c.category, (totals.get(c.category) || 0) + c.spend);
            if (c.spend > biggest)
                biggest = c.spend;
        }
        return {
            at: (cat, fy) => map.get(`${cat}::${fy}`),
            max: biggest,
            rowTotal: (cat) => totals.get(cat) || 0,
        };
    }, [cells]);
    /*
     * HOW MANY YEARS FIT, measured (Ryan, 2026-09-14): "if the size of the
     * widget does not allow for all of the years, then the columns should be
     * truncated to the most recent amount of years that fit into the space."
     *
     * The grid used to scroll instead, which put the OLDEST year at the left
     * edge and left it half-cut — the reader saw "Y20" and had to drag to reach
     * the year they actually care about. Dropping from the left keeps the
     * newest years, which is the direction a seller reads.
     *
     * Columns are dropped, never narrowed: below COL_MIN a value clips to a
     * lone "$", which is the one thing .vch-val promises never to show.
     */
    const gridRef = useRef(null);
    const [fit, setFit] = useState(years.length);
    const measure = React.useCallback(() => {
        const el = gridRef.current;
        if (!el || !years.length)
            return;
        // No layout yet — jsdom, or the first paint in some browsers. An
        // UNMEASURED container must not hide data: fall back to showing every
        // year rather than computing a fit from a zero width, which resolves to
        // one column and quietly drops the rest.
        const width = el.clientWidth;
        if (!width) {
            setFit(years.length);
            return;
        }
        const n = Math.max(1, Math.floor((width - LABEL_MIN) / COL_MIN));
        setFit(Math.min(years.length, n));
    }, [years.length]);
    useLayoutEffect(measure);
    useEffect(() => {
        const el = gridRef.current;
        if (!el || typeof ResizeObserver === 'undefined')
            return undefined;
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [measure]);
    const shownYears = years.slice(-Math.max(1, Math.min(fit, years.length)));
    /*
     * ONE COLUMN IS NOT A MATRIX, so it stops pretending to be one.
     *
     * A heatmap earns its grid by letting the eye compare across BOTH axes. With
     * a single fiscal year in the window there is no second axis, and what the
     * reader gets is three full-width blocks carrying one number each — on DGS
     * at 3y, $7K / $6K / $4K rendered as 625px slabs, which reads as a large
     * footprint and is in fact $17K.
     *
     * With one period the useful comparison is between CATEGORIES, and length
     * carries that far better than a colour wash. So the cells become bars.
     * Linear, not the sqrt the colour uses: sqrt exists to stop one outlier
     * flattening a colour scale, but a bar whose length is sqrt-scaled overstates
     * every small value and is simply a wrong chart.
     */
    const singlePeriod = shownYears.length === 1;
    const hiddenYears = years.length - shownYears.length;
    if (!categories.length || !years.length) {
        return (_jsx("div", { className: `chs__empty${fill ? ' chs__empty--fill' : ''}`, style: fill ? undefined : { '--h': `${height}px` }, children: "No product spend recorded for this vendor at this agency." }));
    }
    return (_jsxs("div", { className: `vch${fill ? ' chfill' : ''}`, children: [_jsxs("div", { ref: gridRef, className: `vch-grid vch-grid--dynamic${singlePeriod ? ' vch-grid--single' : ''}`, style: { '--vch-cols': shownYears.length }, children: [_jsx("span", { className: "vch-corner", "aria-hidden": "true" }), shownYears.map((y) => (_jsx("span", { className: "vch-head", children: fyLabel(y) }, y))), categories.map((cat) => (_jsxs(React.Fragment, { children: [_jsxs("span", { className: "vch-row-label", title: `${cat} — ${formatValue(rowTotal(cat))} total`, children: [_jsx("i", { "aria-hidden": "true", className: "vch-swatch", style: { '--c': securityCategoryColor(cat) } }), cat] }), shownYears.map((y) => {
                                const cell = at(cat, y);
                                const spend = cell ? cell.spend : 0;
                                /*
                                 * Square root, not linear. One enormous cell otherwise flattens
                                 * every other one to the same near-white and the pattern — which
                                 * is the entire point of a heatmap — disappears into the
                                 * outlier. The rhythm calendar learned this the hard way.
                                 */
                                const i = max > 0 && spend > 0 ? Math.sqrt(spend / max) : 0;
                                /*
                                 * The BAR's length is linear, where the colour is sqrt.
                                 * sqrt exists so one outlier cannot flatten a colour scale; a bar
                                 * scaled that way would draw $4K of $7K at 76% instead of 57% and
                                 * overstate every small value. Two encodings, two scales.
                                 * (Kept out of the style object: chartInlineStyles parses that
                                 * literal and reads a comment inside it as a stray property.)
                                 */
                                const ratio = max > 0 && spend > 0 ? spend / max : 0;
                                const empty = !(spend > 0);
                                const label = empty
                                    ? `${cat}, ${fyLabel(y)} — nothing recorded`
                                    : `${cat}, ${fyLabel(y)} — ${formatValue(spend)}${cell?.lineCount ? ` across ${cell.lineCount} lines` : ''}`;
                                const Tag = (onPick && !empty ? 'button' : 'span');
                                return (_jsx(Tag, { type: onPick && !empty ? 'button' : undefined, className: `vch-cell${empty ? ' vch-cell--empty' : ''}`, style: empty ? undefined : {
                                        '--c': securityCategoryColor(cat),
                                        '--i': i.toFixed(3),
                                        '--r': ratio.toFixed(4),
                                    }, title: label, "aria-label": label, onClick: onPick && !empty && cell ? () => onPick(cell) : undefined, children: !empty && _jsx("span", { className: "vch-val", children: formatValue(spend) }) }, y));
                            })] }, cat)))] }), resellers.length > 0 && (_jsxs("p", { className: "vch-channel cq-hide-glance", children: [_jsx("span", { className: "vch-channel-label", children: "Sold through" }), resellers.slice(0, 3).map((r) => (_jsxs("span", { className: "vch-channel-item", children: [r.supplier, " ", _jsx("b", { children: formatValue(r.spend) })] }, r.supplier)))] })), _jsxs("p", { className: "ts-foot", children: [scopeNote || 'Vendor product bought by this agency, by fiscal year', max > 0 && (_jsx("span", { className: "vch-foot-scale", children: singlePeriod
                            // With one period the value is in the BAR's length, not the wash,
                            // so the note has to name the encoding actually in use.
                            ? ` · full bar = ${formatValue(max)}`
                            : ` · full colour = ${formatValue(max)}` })), hiddenYears > 0 && (_jsx("span", { className: "vch-foot-trunc", children: ` · ${fyLabel(years[0])}–${fyLabel(years[hiddenYears - 1])} hidden, too narrow to draw` }))] })] }));
};
export default VendorCategoryHeatmap;
